import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';

import { BylineConnectionIconStack } from './BylineConnectionIconStack.tsx';
import {
	type BylineConnectionIcon,
	MAX_BYLINE_CONNECTION_ICONS,
} from './utils/getBylineConnectionIcons.ts';

const i18n = defineMessages({
	loadingConnections: {
		id: 'byline-connections.loading',
		defaultMessage: 'Loading connections',
		description: 'Accessible label for the connections byline loading spinner',
	},
	teamworkGraph: {
		id: 'byline-connections.teamwork-graph-label',
		defaultMessage: 'Teamwork Graph',
		description: 'Label for the Teamwork Graph byline item shown in the page byline area',
	},
});

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		marginInlineEnd: token('space.050'),
		height: '24px',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		whiteSpace: 'normal',
	},
	trigger: {
		display: 'inline-flex',
		alignItems: 'center',
		color: token('color.text.disabled'),
		font: token('font.body.small'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		padding: token('space.050'),
		whiteSpace: 'nowrap',
	},
});

const createPlaceholderIcon = (index: number): BylineConnectionIcon => ({
	key: `placeholder-link-${index}`,
	label: '',
	icon: {
		kind: 'element',
		key: `placeholder-link-${index}`,
		label: '',
		node: (
			<LinkIcon
				label=""
				color={token('color.icon.disabled')}
				testId="byline-connections-ssr-placeholder-icon"
			/>
		),
	},
});

type BylineConnectionsPlaceholderVisualProps = {
	testId?: string;
	// Number of disabled tiles to render; defaults to the maximum when the count is unknown. Pass 0
	// for the text-only (label, no tiles) mode used by the inbound-only placeholder.
	iconCount?: number;
};

/**
 * The disabled icon-stack + "Teamwork Graph" placeholder shown while the byline
 * connections item is resolving. Shared by the SSR/loadable placeholder and the
 * loaded component's own loading state so the footprint lines up across the
 * SSR → client-load → data-load → item handoff, avoiding layout shift. It renders
 * `iconCount` disabled tiles — sized from the outbound domain estimate — so the
 * stack matches the number of icons the loaded byline is expected to show. An
 * `iconCount` of 0 renders the label alone, matching the inbound-only trigger,
 * which shows no icon stack.
 */
export const BylineConnectionsPlaceholderVisual = ({
	testId = 'byline-connections-ssr-placeholder',
	iconCount = MAX_BYLINE_CONNECTION_ICONS,
}: BylineConnectionsPlaceholderVisualProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const placeholderIcons = useMemo(
		() =>
			Array.from(
				{ length: Math.min(Math.max(iconCount, 0), MAX_BYLINE_CONNECTION_ICONS) },
				(_, index) => createPlaceholderIcon(index),
			),
		[iconCount],
	);

	return (
		<span
			css={styles.wrapper}
			role="status"
			aria-label={formatMessage(i18n.loadingConnections)}
			data-testid={testId}
		>
			<span css={styles.trigger} aria-hidden="true">
				<BylineConnectionIconStack
					links={[]}
					additionalIcons={placeholderIcons}
					appearance="placeholder"
				/>
				{formatMessage(i18n.teamworkGraph)}
			</span>
		</span>
	);
};
